const STORAGE_KEY = "pendingSignup";
const VERIFICATION_KEY = "pendingVerificationEmail";

export type SignupMethod = "email" | "google";

/** Matches the backend `YESnNO` enum — anything else fails validation on POST /users. */
export type YesNo = "Yes" | "No";

/**
 * Where a resume-upload setup has got to.
 *
 * The flow spans three requests (POST /users -> POST smart-profiles -> presigned PUT) and a poll,
 * so a refresh at any point has to be recoverable. A set of booleans would allow states that
 * cannot exist; one phase cannot.
 *
 * - `account` — POST /users is in flight, or the page died before its response was read.
 * - `dsp`     — the user record exists, the DSP row does not.
 * - `upload`  — the DSP row exists, the file has not reached S3.
 * - `extract` — the file is in S3; polling for the extraction to finish.
 */
export type ResumeSetupPhase = "account" | "dsp" | "upload" | "extract";

export interface ResumeSetupState {
	phase: ResumeSetupPhase;
	dspId?: string;
	/** Kept so a recovery attempt can tell "same file" from "different file". */
	fileHash?: string;
	/**
	 * When polling started, in epoch ms. Persisted rather than held in a timer so that reloading
	 * during extraction cannot hand the user a fresh "under a minute" every time.
	 */
	startedAt?: number;
}

/**
 * Draft of the account being created, handed from /register -> /onboarding -> /creating-profile.
 * Replaces the old pre-auth "savedPreferences" blob, which held the whole questionnaire.
 */
export interface PendingSignup {
	firstName: string;
	lastName: string;
	email: string;
	phone?: string;
	method: SignupMethod;
	/** Filled in by the onboarding step. */
	jobTitle?: string;
	areasOfExpertise?: string[];
	recentGraduate?: YesNo;
	/** Month name as shown in the dropdown; converted to a number for the API. */
	experienceMonth?: string;
	experienceYear?: string;
	/** Asked outright; ./seniority still derives it for drafts written before the question existed. */
	seniorityLevel?: string;
	/** "District,Region", from the same vocabulary the resume builder's address field uses. */
	location?: string;
	/**
	 * Set when the draft was rebuilt after sign-in for an account whose backend record went missing
	 * (see services/auth/accountRecovery). Those users are already signed in and confirmed, so
	 * /creating-profile sends them straight to their profile instead of asking them to check their
	 * email for a link they followed long ago.
	 */
	emailVerified?: boolean;
	/**
	 * Present only while a resume upload is being set up. Its presence — at any phase — tells
	 * /onboarding not to bounce the user to /profile just because the backend record now exists.
	 */
	resumeSetup?: ResumeSetupState;
}

export function getPendingSignup(): PendingSignup | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? (JSON.parse(raw) as PendingSignup) : null;
	} catch {
		return null;
	}
}

export function setPendingSignup(pendingSignup: PendingSignup) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingSignup));
}

export function clearPendingSignup() {
	localStorage.removeItem(STORAGE_KEY);
}

/**
 * Advance the resume setup and persist it in one step.
 *
 * Always called *before* the request the new phase describes, never after. A page closed between
 * a request and its response must leave the draft pointing at the step that may already have
 * happened, not at the step before it.
 */
export function setResumeSetup(resumeSetup: ResumeSetupState): PendingSignup | null {
	const pendingSignup = getPendingSignup();
	if (!pendingSignup) return null;

	const updated = { ...pendingSignup, resumeSetup };
	setPendingSignup(updated);
	return updated;
}

export function clearResumeSetup() {
	const pendingSignup = getPendingSignup();
	if (!pendingSignup?.resumeSetup) return;

	const { resumeSetup: _discarded, ...rest } = pendingSignup;
	setPendingSignup(rest);
}

/**
 * Set once the account exists but the email link hasn't been followed yet. Keeps the
 * "check your email" screen (and its resend button) reachable across a page refresh without
 * re-POSTing the user.
 */
export function setPendingVerificationEmail(email: string) {
	localStorage.setItem(VERIFICATION_KEY, email);
}

export function getPendingVerificationEmail(): string | null {
	return localStorage.getItem(VERIFICATION_KEY);
}

export function clearPendingVerificationEmail() {
	localStorage.removeItem(VERIFICATION_KEY);
}
